/**
 * useOrders — restaurant-wide transaction list for the owner.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import * as repository from "@/data/orderRepository";
import { useAuth } from "../../../../lib/auth";
import { api, isMockApi } from "@/lib/api";

function toClientOrder(order) {
  if (!order) return null;
  return {
    ...order,
    code: order.code || order.orderNumber,
    customer: order.customer
      ? { ...order.customer, phone: order.customer.phone || order.customer.mobile || "", address: order.customer.address || order.deliveryAddress || "" }
      : { name: "Guest", phone: "", address: order.deliveryAddress || "" },
    items: (order.items || []).map((item) => ({ ...item, qty: item.qty ?? item.quantity ?? 0 })),
    subtotal: order.subtotal ?? order.subtotalAmount ?? 0,
    tax: order.tax ?? order.taxAmount ?? 0,
    total: order.total ?? order.totalAmount ?? 0,
  };
}

/** Keep the client draft's composite identity while only sending API fields. */
export function toVendorOrderLine({ id: menuItemId, menuItemSizeId, qty: quantity }) {
  return { menuItemId, ...(menuItemSizeId ? { menuItemSizeId } : {}), quantity };
}

export default function useOrders({ storefrontId } = {}) {
  const { getOrders, updateOrderStatus, cancelOrder, completeTable, addOrderItems, getAllowedStorefronts } = repository;
  const { vendor, refresh } = useAuth();
  const [tick, setTick] = useState(0);
  const [serverOrders, setServerOrders] = useState([]);

  const scopedStoreIds = useMemo(() => {
    const allowed = getAllowedStorefronts(vendor).map((s) => s.id);
    if (storefrontId && storefrontId !== "all") {
      return allowed.filter((id) => id === storefrontId);
    }
    return allowed;
  }, [vendor, storefrontId]);

  const mockOrders = useMemo(() => {
    if (!vendor) return [];
    const all = getOrders(vendor.id);
    const legacyFallbackId = vendor.storefronts?.[0]?.id;
    return all.filter((o) => {
      const sid = o.storefrontId || legacyFallbackId;
      return scopedStoreIds.includes(sid);
    });
    // tick forces recompute after status updates
  }, [vendor, scopedStoreIds, tick]);

  const reload = useCallback(async () => {
    if (isMockApi) {
      setTick((value) => value + 1);
      return;
    }
    const result = await api.listVendorOrders({ storefrontId });
    setServerOrders(result.map(toClientOrder));
  }, [storefrontId]);

  useEffect(() => {
    if (!isMockApi) reload().catch(() => setServerOrders([]));
  }, [reload]);

  const orders = isMockApi ? mockOrders : serverOrders;

  const setStatus = useCallback(
    async (orderId, status) => {
      if (!isMockApi) {
        const updated = toClientOrder(await api.updateVendorOrderStatus(orderId, status));
        setServerOrders((current) => current.map((order) => (
          order.id === orderId ? { ...order, ...updated, items: order.items } : order
        )));
        return updated;
      }
      const o = updateOrderStatus(orderId, status);
      setTick((t) => t + 1);
      refresh();
      return o;
    },
    [refresh],
  );

  const mutate = useCallback((operation, ...args) => {
    const result = operation(...args);
    setTick((value) => value + 1);
    return result;
  }, []);
  return {
    orders,
    setStatus,
    cancel: async (id) => {
      if (isMockApi) return mutate(cancelOrder, id);
      const updated = toClientOrder(await api.updateVendorOrderStatus(id, "cancelled"));
      setServerOrders((current) => current.map((order) => (order.id === id ? { ...order, ...updated, items: order.items } : order)));
      return updated;
    },
    closeTable: async (id) => {
      if (isMockApi) return mutate(completeTable, id);
      const updated = toClientOrder(await api.closeVendorOrderTable(id));
      setServerOrders((current) => current.map((order) => (order.id === id ? { ...order, ...updated, items: order.items } : order)));
      return updated;
    },
    appendItems: async (id, items) => {
      if (isMockApi) return mutate(addOrderItems, id, items);
      const updated = toClientOrder(await api.addVendorOrderItems(id, items.map(toVendorOrderLine)));
      setServerOrders((current) => current.map((order) => (order.id === id ? updated : order)));
      return updated;
    },
    reload,
    getDetail: async (id) => isMockApi ? orders.find((order) => order.id === id) || null : toClientOrder(await api.getVendorOrder(id)),
    allowedStorefrontIds: scopedStoreIds,
  };
}

export { toClientOrder };
